import { useState } from 'react';
import { Campaign } from '../config/campaigns';

interface CampaignCardProps {
    readonly campaign: Campaign;
    readonly onDonate: (campaign: Campaign) => void;
}

export function CampaignCard({ campaign, onDonate }: CampaignCardProps) {
    const [copied, setCopied] = useState(false);

    const handleShare = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (campaign.isExample) return;
        const url = `${window.location.origin}${window.location.pathname}?campaign=${campaign.id}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const progress =
        campaign.goalBTC > 0
            ? Math.min((campaign.raisedBTC / campaign.goalBTC) * 100, 100)
            : 0;
    const isAlmostFunded = progress >= 80;

    const gradients: Record<string, string> = {
        Education: 'linear-gradient(135deg, #06d6a0, #0891b2)',
        'Open Source': 'linear-gradient(135deg, #7c3aed, #a855f7)',
        Security: 'linear-gradient(135deg, #ef4444, #f97316)',
        Infrastructure: 'linear-gradient(135deg, #38bdf8, #6366f1)',
        Community: 'linear-gradient(135deg, #fbbf24, #f97316)',
        Research: 'linear-gradient(135deg, #06b6d4, #7c3aed)',
        Charity: 'linear-gradient(135deg, #ec4899, #f43f5e)',
        'Art & Culture': 'linear-gradient(135deg, #a855f7, #ec4899)',
    };

    const gradient =
        gradients[campaign.category] ??
        'linear-gradient(135deg, var(--surface-overlay), var(--surface-interactive))';

    return (
        <div
            className="campaign-card"
            onClick={() => onDonate(campaign)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onDonate(campaign);
            }}
        >
            <div
                className="campaign-card__image-placeholder"
                style={{ background: gradient }}
            >
                <span role="img" aria-label={campaign.category}>
                    {campaign.icon}
                </span>
            </div>

            <div className="campaign-card__body">
                <div
                    style={{
                        display: 'flex',
                        gap: 'var(--space-sm)',
                        alignItems: 'center',
                        marginBottom: 'var(--space-md)',
                    }}
                >
                    <span className="campaign-card__category">{campaign.category}</span>
                    {campaign.isExample && (
                        <span
                            className="campaign-card__category"
                            style={{
                                background: 'rgba(245, 158, 11, 0.15)',
                                color: 'var(--accent-warm)',
                            }}
                        >
                            Example
                        </span>
                    )}
                </div>

                <h3 className="campaign-card__title">{campaign.title}</h3>

                <p className="campaign-card__desc">{campaign.description}</p>

                <div className="progress">
                    <div className="progress__bar-bg">
                        <div
                            className="progress__bar-fill"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <div className="progress__info">
                        <span className="progress__amount">
                            {campaign.raisedBTC.toFixed(4)} BTC raised
                        </span>
                        <span>{progress.toFixed(0)}%</span>
                    </div>
                </div>

                <div className="campaign-card__footer">
                    <div className="campaign-card__stat">
                        <span className="campaign-card__stat-value">
                            {campaign.goalBTC} BTC
                        </span>
                        <span className="campaign-card__stat-label">Goal</span>
                    </div>
                    <div className="campaign-card__stat">
                        <span className="campaign-card__stat-value">{campaign.backers}</span>
                        <span className="campaign-card__stat-label">Backers</span>
                    </div>
                    <div className="campaign-card__stat">
                        <span
                            className="campaign-card__stat-value"
                            style={{
                                color: isAlmostFunded
                                    ? 'var(--color-warning)'
                                    : 'var(--text-primary)',
                            }}
                        >
                            {campaign.durationDays}d
                        </span>
                        <span className="campaign-card__stat-label">Duration</span>
                    </div>
                    {!campaign.isExample && (
                        <button
                            className="campaign-card__share-btn"
                            onClick={handleShare}
                            title="Copy campaign link"
                        >
                            {copied ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                    <polyline points="16 6 12 2 8 6" />
                                    <line x1="12" y1="2" x2="12" y2="15" />
                                </svg>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
